// Reading Deluxe timer-mode overlay.
//
// Per Page is opt-in. If an activity has no explicit timerPerPage value,
// use the new whole-activity timer behavior required by Reading Deluxe.
export const isPerPageTimerMode = (options, gameData) => {
  // Per Page is an explicit opt-in. Early overlay builds accidentally wrote
  // timerPerPage=true as a default, so an unmarked value must not enable the
  // legacy mode. The configured marker is written only after a real checkbox
  // change in the editor.
  if (options?.timerPerPageConfigured === true) {
    return options?.timerPerPage === true;
  }

  // Runtime fallback mirror for launch paths that do not carry data.options.
  if (gameData?.timerPerPageConfigured === true) {
    return gameData?.timerPerPage === true;
  }

  return false;
};

// The published language version can lag behind the base timer settings.
// Inherit only absent fields; explicitly configured localized values win.
export const inheritReadingTimerMetadata = (page, basePage) => {
  if (!page || !basePage) return page;

  const result = { ...page };
  ['hasTimer', 'benchmark', 'totalWords'].forEach(field => {
    if ((result[field] === undefined || result[field] === null || result[field] === '') &&
        basePage[field] !== undefined && basePage[field] !== null && basePage[field] !== '') {
      result[field] = basePage[field];
    }
  });
  return result;
};

export const isUsableTimerPage = page =>
  !!page?.hasTimer &&
  Number.isFinite(Number(page?.benchmark)) && Number(page?.benchmark) > 0 &&
  Number.isFinite(Number(page?.totalWords)) && Number(page?.totalWords) > 0;

export const getTimerPageIndex = ({ options, gameData, pages, currentPageIdx }) => {
  const selectedPages = Array.isArray(pages) ? pages : [];
  if (!selectedPages.length) return -1;

  if (isPerPageTimerMode(options, gameData)) {
    return isUsableTimerPage(selectedPages[currentPageIdx]) ? currentPageIdx : -1;
  }

  // A cover/intro page may precede the first timed Reading page. Find the
  // first valid configured timer rather than assuming it is pages[0].
  const firstTimedIndex = selectedPages.findIndex(page => !!page?.hasTimer);
  return firstTimedIndex >= 0 && isUsableTimerPage(selectedPages[firstTimedIndex])
    ? firstTimedIndex
    : -1;
};

export const getTimerPage = args => {
  const index = getTimerPageIndex(args);
  return index >= 0 ? args.pages[index] : null;
};

export const hasTimerForCurrentMode = args => {
  const timerPageIndex = getTimerPageIndex(args);
  if (timerPageIndex < 0) return false;
  return isPerPageTimerMode(args.options, args.gameData) ||
    args.currentPageIdx >= timerPageIndex;
};

export const shouldStartTimerOnCurrentPage = args => {
  if (!hasTimerForCurrentMode(args)) return false;
  return isPerPageTimerMode(args.options, args.gameData) ||
    args.currentPageIdx === getTimerPageIndex(args);
};

export const shouldShowTimer = args => hasTimerForCurrentMode(args);

export const shouldShowTimerStart = args =>
  !args.timerStarted && shouldStartTimerOnCurrentPage(args);

export const shouldShowTimerStop = args => {
  if (!args.timerStarted || !hasTimerForCurrentMode(args)) return false;
  return isPerPageTimerMode(args.options, args.gameData) || !!args.isLastPage;
};

export const shouldHideNextButton = ({
  options,
  gameData,
  timerStarted,
  isLastPage,
  isReady = true
}) => {
  // ActivityButtons renders once before Reading Deluxe init() has populated
  // pages/currentPage. Hide NEXT during that transient render so the Global
  // Bar never flashes a navigation action that may be hidden after init.
  if (!isReady) return true;

  if (isLastPage) return true;

  // Preserve the legacy per-page behavior: the learner must stop the current
  // page timer before moving on. Whole-activity mode keeps the timer running
  // while the learner advances through the pages.
  return isPerPageTimerMode(options, gameData) && !!timerStarted;
};

export const getEditorTimerPageIndex = ({
  options,
  gameData,
  activeProblemIdx
}) =>
  isPerPageTimerMode(options, gameData) ? Number(activeProblemIdx) : 0;

export const validateProblemForTimerMode = ({
  options,
  gameData,
  problem,
  pageIndex,
  validateProblem,
  allowIncompleteTimer = false
}) => {
  if (typeof validateProblem !== 'function') return false;

  // Runtime must not silently drop an otherwise readable page if its
  // published timer metadata is incomplete. Editor validation stays strict.
  if (allowIncompleteTimer) {
    return validateProblem({
      ...problem,
      text: Array.isArray(problem?.text) ? problem.text : [],
      hasTimer: false
    });
  }

  if (isPerPageTimerMode(options, gameData) || pageIndex === 0) {
    return validateProblem(problem);
  }

  // Switching to whole-activity mode is non-destructive: existing timer data
  // on pages 2+ is kept so it is available if Per Page is enabled again.
  // While whole-activity mode is active, however, those stale page-level timer
  // fields must not invalidate otherwise valid pages.
  return validateProblem({ ...problem, hasTimer: false });
};
